import type { ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowTopRightOnSquareIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  AgencyUserFieldsFragment,
  EmailBulkSendAction,
  MailboxProvider,
  MailboxSharingFieldsFragment,
  type OutboundEmailAmsLogFieldsFragment,
  OutboundEmailFailureKind,
  OutboundEmailFieldsFragment,
  OutboundEmailStatus
} from '~/api/graphql';
import { FieldReferenceText } from '~/components/email-templates/field-reference-text';
import { formatAgencyUserName, rowDateTimeFormatter } from '~/components/table-formatters';
import { Dash } from '~/components/clients/clients-columns';
import { mailClientName } from '~/lib/mailbox-providers';
import { pluralize } from '~/lib/pluralize';
import { formatTimeOmittingSameDay } from '~/lib/scheduled-send';
import { type BatchStatus, batchStatus } from '~/pages/mailbox/outbox-bulk-send-summary';
import { BulkBadge } from './bulk-badge';
import { OutboxAmsCell } from './outbox-ams-cell';
import { bulkDraftPath } from './bulk-send-paths';
import { mailboxDraftState, mailboxDraftWebLink } from './mailbox-drafts-columns';
import {
  outboundEmailRecipientSummary,
  outboundEmailRecipients,
  outboundEmailStatusNote,
  outboundEmailTimestamp
} from './outbound-emails-columns';
import {
  NO_OUTBOX_BATCHES,
  type OutboxBatches,
  type OutboxBulkSend,
  isBulkSendDrafting,
  isOutboxRowHeldBack,
  isOutboxRowQueuedInBatch
} from './outbox-bulk-send-rows';
import {
  OutboxRow,
  OutboxStatus,
  OutboxUnreadSources,
  outboxRowDelivered,
  outboxRowDisplaySend,
  outboxRowSendUnconfirmed,
  type OutboxDraftStaleness,
  outboxRowStaleness,
  outboxRowStatus,
  outboxRowStatusUnsound
} from './outbox-rows';
import {
  OUTBOX_ROW_ACTION_CLASS,
  outboxRowSendTimeText,
  scheduledRowTimestamp,
  scheduledSendStatusNote
} from './outbox-schedule-columns';

/** The reach of a caller that has not said which ledgers went unread. */
const EVERY_LEDGER_READ: OutboxUnreadSources = {};

/** The reach of a caller who has not said which mailboxes it can open. */
const NO_OPENABLE_MAILBOXES: ReadonlySet<string> = new Set();

/** The reach of a caller who has not said which mailboxes went unreachable. */
const NO_DISCONNECTED_MAILBOXES: ReadonlySet<string> = new Set();

/** Used when the columns are built without each mailbox's provider. */
const NO_MAILBOX_PROVIDERS: ReadonlyMap<string, MailboxProvider> = new Map();
const NO_MAILBOX_ADDRESSES: ReadonlyMap<string, string> = new Map();

const STATUS_LABEL: Record<OutboxStatus, string> = {
  checking: 'Checking',
  mailboxDisconnected: 'Mailbox unavailable',
  composed: 'Composed',
  inMailbox: 'In drafts',
  scheduled: 'Scheduled',
  sending: 'Sending',
  cancelled: 'Send cancelled',
  interrupted: 'Interrupted',
  sent: 'Sent',
  sentFromMailClient: 'Sent from mail client',
  failed: 'Failed',
  replaced: 'Replaced',
  withdrawn: 'Withdrawn',
  goneUnproven: 'Gone from drafts',
  closed: 'No longer in drafts'
};

/**
 * What the badge says, and — because the filter reads the same table — what the
 * chip that selects the state says. One word per state, with nothing a row can
 * substitute for it: a label that varied by row would let a reader filter on
 * "Composed" and be handed rows badged something else.
 *
 * The artifact version an unsent email stands at is deliberately not here. It
 * says which revision of the words the row opens, which is a fact about the
 * document rather than about how far the email travelled, and the composer the
 * row links to names it on arrival.
 */
export const outboxStatusLabel = (status: OutboxStatus): string => STATUS_LABEL[status];

/** A held-back email's hover once nothing holds it back any more. */
const HELD_BACK_FIXED_TITLE = 'Left out of its batch. Open it to send it on its own.';

/** What a row calls the mail client of a mailbox whose provider isn't known. */
const UNKNOWN_MAIL_CLIENT = 'your mail client';

/**
 * The badge's hover text, for the statuses whose label is accurate but terse.
 *
 * A hover rather than a line under the subject, because a list shows ten of
 * these at once and a sentence repeated down a column stops being read. Most
 * of what they say the row already shows anyway — an email with no recipients,
 * no sender and nothing to open is visibly not out in the world — so what is
 * left is worth reaching for, not worth printing.
 */
const STATUS_TITLE: Partial<Record<OutboxStatus, string>> = {
  checking: 'Re-reading the mailbox to see whether this draft is still there',
  mailboxDisconnected:
    'Cara cannot access this mailbox to check whether this draft was sent. Check the connection in Settings. If Microsoft has disabled access, ask your administrator to restore it, then sync again.',
  composed: 'Written in Cara and not put in a mailbox, so nobody has it yet.',
  scheduled: 'Cara sends this at the time picked.',
  sentFromMailClient:
    'A sent copy of this mail is in the mailbox, so the recipient has it. It went out from the mail client rather than through Cara, which is why Cara has no delivery record of it.',
  replaced: 'Replaced by a newer draft of the same email.',
  withdrawn: 'Withdrawn from the mailbox by Cara.',
  goneUnproven:
    'Gone from the Drafts folder with nothing in the mailbox to say it was sent. Cara cannot tell a deleted draft from a send it could not match.',
  closed: 'The mailbox let this draft go for a reason this version of Cara does not recognize.'
};

/**
 * Colour carries how settled the row is, not which source it came from: green
 * for mail that reached someone, sky for mail still waiting to be sent, red for
 * a send that failed, zinc for the ones that ended without going anywhere, and
 * amber for the ones where the outcome is genuinely unknown.
 */
const STATUS_COLOR: Record<OutboxStatus, 'sky' | 'blue' | 'amber' | 'green' | 'red' | 'zinc'> = {
  checking: 'zinc',
  // Amber for the same reason interrupted is: the outcome is genuinely unknown
  // and stays unknown until somebody acts.
  mailboxDisconnected: 'amber',
  composed: 'zinc',
  inMailbox: 'sky',
  scheduled: 'sky',
  sending: 'blue',
  cancelled: 'zinc',
  interrupted: 'amber',
  sent: 'green',
  sentFromMailClient: 'green',
  failed: 'red',
  replaced: 'zinc',
  withdrawn: 'zinc',
  goneUnproven: 'amber',
  closed: 'zinc'
};

/**
 * Not "Failed": red would tell a reader the mail did not go out, which is the
 * one thing this state cannot promise.
 */
const UNCONFIRMED_LABEL = 'Unconfirmed';

/**
 * A modifier over the status rather than a status of its own: it describes what
 * Cara knows rather than where the email got to, and there is no such row on
 * the server to filter for.
 */
const UNKNOWN_LABEL = 'Unknown';

const UNKNOWN_TITLE =
  'One of the ledgers this row is read from could not be loaded, so Cara cannot say how far this email got. Reload to try again.';

const UNCONFIRMED_NOTE =
  'Cara asked the mail provider to send this and never got an answer, so it may already be with the recipient. Check the mailbox’s Sent folder before sending it again.';

/**
 * What each classified failure is called, in the words of what the reader can
 * do about it. Chosen by kind rather than by reading `failureReason`, which is
 * prose the server is free to reword and which on older rows is the provider's
 * own diagnostic — a Graph URL and an OData parser complaint, in front of an
 * insurance agent.
 *
 * INTERRUPTED is absent on purpose. Those rows are written by recovery, which
 * knows something no fixed sentence here could say — whether the message ever
 * reached the provider — and says it in the reason it stores.
 */
const FAILURE_HEADLINE: Record<
  Exclude<OutboundEmailFailureKind, OutboundEmailFailureKind.Interrupted>,
  string
> = {
  [OutboundEmailFailureKind.ReauthRequired]:
    'This mailbox needs to be reconnected before it can send.',
  [OutboundEmailFailureKind.ConsentRequired]:
    "Sending from this mailbox needs an administrator's approval.",
  [OutboundEmailFailureKind.AccountUnavailable]: 'There is no usable mailbox behind this account.',
  [OutboundEmailFailureKind.PermissionDenied]:
    'This mailbox connection is missing a permission the send needs.',
  [OutboundEmailFailureKind.NotFound]:
    'The mailbox no longer holds the draft this send was made from.',
  [OutboundEmailFailureKind.TooLarge]:
    'This email is larger than the mail provider accepts. Remove or shrink an attachment, then send again.',
  [OutboundEmailFailureKind.Throttled]:
    'The mail provider is rate-limiting this mailbox. Sending again shortly should work.',
  [OutboundEmailFailureKind.Transient]:
    'The mail provider was temporarily unavailable. Sending again should work.',
  [OutboundEmailFailureKind.Unsupported]:
    'The mail provider does not support something this email needs.',
  [OutboundEmailFailureKind.InvalidRecipients]:
    'Outlook refused one or more recipient addresses. Check them and send again.',
  [OutboundEmailFailureKind.Unknown]: 'The mail provider refused this send.',
  [OutboundEmailFailureKind.Unrecorded]:
    'The mail provider refused this send, and Cara did not record why.'
};

/**
 * What a failure of this kind is called, or undefined for INTERRUPTED, whose stored reason is
 * the only account of it.
 */
export const outboundFailureHeadline = (kind: OutboundEmailFailureKind): string | undefined =>
  kind === OutboundEmailFailureKind.Interrupted ? undefined : FAILURE_HEADLINE[kind];

/**
 * What a failed row says about itself, in two registers: a sentence for the
 * agent, and the ledger's stored text for whoever is diagnosing it.
 *
 * The detail is not the provider's error dressed up — it is what the ledger
 * literally holds, which for a row this build wrote is the same sentence the
 * headline already gives (and is therefore dropped) and for an older row is
 * the raw provider response. Support needs to reach that; nobody needs to read
 * it by default.
 *
 * Null for anything that did not fail, including a send still in flight.
 */
export const outboxRowFailure = (
  row: OutboxRow,
  opts: { nowMs: number }
): { headline: string; detail: string | null } | null => {
  if (outboxRowStatus(row, opts) !== 'failed' || !row.outbound) {
    return null;
  }
  const stored = row.outbound.failureReason.trim();
  const kind = row.outbound.failureKind ?? OutboundEmailFailureKind.Unrecorded;

  // The one failure whose headline must not describe what went wrong, because
  // what went wrong is not the point: nobody knows whether the mail went out.
  if (outboxRowSendUnconfirmed(row)) {
    return { headline: UNCONFIRMED_NOTE, detail: stored || null };
  }
  if (kind === OutboundEmailFailureKind.Interrupted) {
    return {
      headline: stored || FAILURE_HEADLINE[OutboundEmailFailureKind.Unrecorded],
      detail: null
    };
  }

  const headline = FAILURE_HEADLINE[kind] ?? FAILURE_HEADLINE[OutboundEmailFailureKind.Unrecorded];
  return { headline, detail: stored && stored !== headline ? stored : null };
};

/**
 * The line under the subject. Null wherever the badge and the empty cells
 * beside it already say it, which is nearly everywhere. What survives is the
 * two facts a row cannot show: why a send failed, and how long an interrupted
 * one has been unresolved.
 */
export const outboxRowNote = (row: OutboxRow, opts: { nowMs: number }): string | null => {
  const failure = outboxRowFailure(row, opts);
  const scheduledFor = row.outbound?.scheduledFor;
  if (failure && scheduledFor && !outboxRowSendUnconfirmed(row)) {
    return `Not sent at ${formatTimeOmittingSameDay(new Date(scheduledFor), new Date(opts.nowMs))}. ${failure.headline}`;
  }
  if (failure) {
    return failure.headline;
  }
  if (outboxRowStatus(row, opts) === 'interrupted' && row.outbound) {
    return outboundEmailStatusNote(row.outbound, opts.nowMs);
  }
  return null;
};

/** The ledger's own text behind the headline, for whoever is diagnosing the row. */
export const outboxRowFailureDetail = (row: OutboxRow, opts: { nowMs: number }): string | null =>
  outboxRowFailure(row, opts)?.detail ?? null;

/**
 * The attempts before the one the row reports, as a sentence.
 *
 * A retry writes a new send row and leaves the earlier one standing; one row
 * per email means the earlier attempts are reported here rather than given rows
 * of their own.
 *
 * Each outcome is reported in the words of what it means for the recipient,
 * and in that order: a copy they have outranks a copy they may have, which
 * outranks an attempt that plainly never reached them. Reporting only the
 * failures would let the newest attempt decide what the row admits to.
 */
export const outboxRowAttemptNote = (row: OutboxRow): { label: string; title: string } | null => {
  const delivered = row.earlierSends.filter((send) => send.status === OutboundEmailStatus.Sent);
  // An abandoned SENDING attempt will never resolve, so it counts as
  // unaccounted for rather than in flight.
  const unaccounted = row.earlierSends.filter(
    (send) => send.sendUnconfirmed || send.status === OutboundEmailStatus.Sending
  );
  const failed = row.earlierSends.filter(
    (send) => send.status === OutboundEmailStatus.Failed && !send.sendUnconfirmed
  );
  if (delivered.length + unaccounted.length + failed.length === 0) {
    return null;
  }

  const when = (sends: OutboundEmailFieldsFragment[]) =>
    rowDateTimeFormatter.format(new Date(sends[0].createdAt));
  const sentences: string[] = [];
  if (delivered.length === 1) {
    sentences.push(
      `An earlier attempt to send this went out on ${when(delivered)}, so the recipient already has a copy.`
    );
  } else if (delivered.length > 1) {
    sentences.push(
      `${delivered.length} earlier attempts to send this went out, the most recent on ${when(delivered)}, so the recipient already has a copy.`
    );
  }
  if (unaccounted.length === 1) {
    sentences.push(
      `An earlier attempt to send this on ${when(unaccounted)} was never accounted for, so the recipient may already have it.`
    );
  } else if (unaccounted.length > 1) {
    sentences.push(
      `${unaccounted.length} earlier attempts to send this were never accounted for, the most recent on ${when(unaccounted)}, so the recipient may already have it.`
    );
  }
  if (failed.length === 1) {
    sentences.push(`An earlier attempt to send this failed on ${when(failed)}.`);
  } else if (failed.length > 1) {
    sentences.push(
      `${failed.length} earlier attempts to send this failed, the most recent on ${when(failed)}.`
    );
  }

  const label = sentences.join(' ');
  // Why it failed goes behind the line rather than on it. The line's job is to
  // stop a green "Sent" row from reading as a first-time success; the reason is
  // for whoever then asks what happened.
  const reason = [...failed, ...unaccounted].map((send) => send.failureReason.trim()).find(Boolean);
  return { label, title: reason || label };
};

/**
 * The line that says the mailbox is holding words Cara has since moved past,
 * which is a warning rather than a status: the draft is parked exactly where
 * the badge says it is, and an agent who opens it in Outlook and presses send
 * would send the older copy.
 */
export const outboxRowStaleNote = (row: OutboxRow): string | null => {
  switch (outboxRowStaleness(row)) {
    case 'artifactVersionAhead':
      return 'Cara has written a newer version of this email since. Sending the mailbox copy would send the older words; save it to the mailbox again to replace it.';
    case 'contentChanged':
      return 'This email has been edited since it was saved to the mailbox. Sending the mailbox copy would send the older words; save it to the mailbox again to replace it.';
    default:
      return null;
  }
};

/** Provider edits are independent of Cara's artifact-to-draft comparison. */
export const outboxRowExternalEditNote = (row: OutboxRow): string | null => {
  const draft = row.draft;
  if (!draft || mailboxDraftState(draft) !== 'live' || !draft.externallyEdited) return null;
  const checked = draft.externalEditsCheckedAt
    ? ` Last checked ${rowDateTimeFormatter.format(new Date(draft.externalEditsCheckedAt))}.`
    : '';
  return `Edited in your mail client.${checked} Saving from Cara again will replace these edits.`;
};

/**
 * How loudly the row's notices read: red for a send that plainly did not go
 * out, amber for the ones where something is unresolved or the mailbox copy is
 * behind, and neutral for a history note on a row that is otherwise settled.
 */
export type OutboxNoticeTone = 'failure' | 'warning' | 'neutral';

type OutboxNoticeOptions = {
  nowMs: number;
  checking?: boolean;
  mailboxDisconnected?: boolean;
  unknown?: boolean;
  /** The mailbox's mail client, named in a stale copy's short form. */
  mailClient?: string;
};

/**
 * One condition for an editable mailbox draft, without changing its lifecycle
 * status or the server-backed filters. External edits take precedence, but a
 * stale Cara copy still belongs in the same warning so replacing the mailbox
 * copy never hides either risk.
 */
export const outboxRowDraftCondition = (
  row: OutboxRow,
  opts: OutboxNoticeOptions
): { kind: 'external' | 'stale'; note: string } | null => {
  if (opts.unknown || outboxRowStatus(row, opts) !== 'inMailbox') {
    return null;
  }
  const external = outboxRowExternalEditNote(row);
  const stale = outboxRowStaleNote(row);
  if (external) {
    return { kind: 'external', note: [external, stale].filter(Boolean).join(' ') };
  }
  return stale ? { kind: 'stale', note: stale } : null;
};

/**
 * Everything the row has to say beyond its status, most pressing first.
 *
 * One list rather than four stacked lines, because a table row has to be the
 * same height as the row above it: a cell that grows a line whenever an email
 * happens to be stale, or to have failed twice, turns a list somebody is
 * skimming into a ragged column. The cell prints the first of these and carries
 * the rest on hover, so nothing here is lost — only the ones a reader has asked
 * for are given room.
 *
 * The ledger's own failure text comes after the sentences written for an agent,
 * and an earlier attempt's stored reason comes last: both are for whoever is
 * diagnosing the row rather than reading it.
 */
export const outboxRowNotices = (
  row: OutboxRow,
  opts: OutboxNoticeOptions
): { lines: string[]; shownLine: string | null; tone: OutboxNoticeTone } => {
  const ledgerOpts = { nowMs: opts.nowMs };
  const headline = outboxRowNote(row, ledgerOpts);
  const sendTimeText = outboxRowSendTimeText(row, ledgerOpts);
  const draftCondition = outboxRowDraftCondition(row, opts);
  const attempt = outboxRowAttemptNote(row);
  const lines = [
    headline,
    sendTimeText,
    draftCondition?.note,
    attempt?.label,
    outboxRowFailureDetail(row, ledgerOpts),
    attempt && attempt.title !== attempt.label ? attempt.title : null
  ].filter((line): line is string => !!line);

  const failedOutright =
    outboxRowStatus(row, ledgerOpts) === 'failed' && !outboxRowSendUnconfirmed(row);
  const tone: OutboxNoticeTone = headline
    ? failedOutright
      ? 'failure'
      : 'warning'
    : draftCondition
      ? 'warning'
      : 'neutral';

  // A stale copy's warning is too long to read in a row, so the row shows a
  // short form and keeps the sentence on hover.
  const mailClient = opts.mailClient ?? UNKNOWN_MAIL_CLIENT;
  const staleShortForm: Record<OutboxDraftStaleness, string> = {
    contentChanged: `Edited since saved to ${mailClient}`,
    artifactVersionAhead: `Cara rewrote it since saved to ${mailClient}`
  };
  const staleness = outboxRowStaleness(row);
  const shownLine =
    staleness && draftCondition?.kind === 'stale' && lines[0] === draftCondition.note
      ? staleShortForm[staleness]
      : (lines[0] ?? null);

  return { lines, shownLine, tone };
};

/**
 * What the email is called. The send is asked first: its subject is what the
 * provider reported going out, which an agent may have edited after Cara
 * handed the draft over.
 */
export const outboxRowSubject = (row: OutboxRow): string =>
  outboxRowDisplaySend(row)?.subject || row.draft?.subject || row.artifact?.title || '';

/**
 * The address the email went or would go from. An email with no send or draft
 * yet goes from the mailbox chosen in its composer, named only when that
 * mailbox is one the reader can see; with none chosen the composer's default
 * is the reader's own, so it says nothing.
 */
export const outboxRowFromAddress = (
  row: OutboxRow,
  mailboxAddresses: ReadonlyMap<string, string> = NO_MAILBOX_ADDRESSES
): string => {
  const chosenMailboxId = row.artifact?.emailMailboxAccountId;
  const chosenAddress = chosenMailboxId ? mailboxAddresses.get(chosenMailboxId) : undefined;
  return outboxRowDisplaySend(row)?.fromAddress || row.draft?.fromAddress || chosenAddress || '';
};

/**
 * Whose email it is: whoever committed the send, or failing that whoever asked
 * Cara for the draft. Null once that user is offboarded, and on a send Cara
 * reconstructed after the fact, where nobody clicked send in Cara at all.
 */
export const outboxRowUserId = (row: OutboxRow): string | null =>
  row.outbound?.sentByUserId ?? row.draft?.createdByUserId ?? null;

/**
 * The name for the From column, and null where there is no person to name.
 *
 * The two absences are not the same and must not print the same word. A Cara
 * send and a draft each record who asked for them, so a blank there is somebody
 * who has since left the agency, which is what `formatAgencyUserName` says. An
 * email Cara composed and nobody put in a mailbox was never anyone's click to
 * record, and neither was a send reconstructed from a mail client — calling
 * either one an offboarded user would announce a departure that never happened.
 */
export const outboxRowSenderName = (
  row: OutboxRow,
  agencyUsers: Pick<AgencyUserFieldsFragment, 'id' | 'name' | 'email'>[]
): string | null => {
  const acted = !!row.draft || (!!row.outbound && !row.outbound.sentExternally);
  return acted ? formatAgencyUserName(outboxRowUserId(row), agencyUsers) : null;
};

/**
 * Who the email went to, in the words of a cell that may not grow: the first
 * recipient, and how many more there are.
 *
 * An email to a household and its two brokers is as ordinary here as an email
 * to one person, and neither may be allowed to set the width of a column. What
 * a reader can act on from a list is who it went to first and whether it went
 * to a crowd; the roll-call is a hover away.
 *
 * A send names who it went to; an email with no send yet is addressed as its
 * latest version is. Null where neither names anyone.
 */
export const outboxRowRecipients = (
  row: OutboxRow
): { label: string; extra: number; title: string } | null => {
  const draftRecipients = row.artifact?.emailRecipients;
  const hasDraftRecipients =
    !!draftRecipients && outboundEmailRecipients(draftRecipients).length > 0;
  const recipients = outboxRowDisplaySend(row) ?? (hasDraftRecipients ? draftRecipients : null);
  if (!recipients) {
    return null;
  }
  const { label, extra, title } = outboundEmailRecipientSummary(recipients);
  // The count leads, because "+3" asks the reader to do arithmetic before they
  // know whether this went to a person or to a distribution list.
  return { label, extra, title: extra > 0 ? `${extra + 1} recipients: ${title}` : title };
};

/** Opens an email in its workspace, at the version given. */
export const workspaceArtifactHref = (workspaceId: string, artifactId: string, version: number) =>
  `/app/workspaces/${workspaceId}?artifact_id=${artifactId}&version=${version}`;

/**
 * Where the row leads: the artifact version this email stands at, which is the
 * copy of the words Cara can still show. Undefined where the workspace is
 * gone, which the caller renders as an unclickable row rather than a dead link.
 */
export const outboxRowArtifactHref = (row: OutboxRow): string | undefined => {
  // A bulk email's row opens its bulk draft page, before Send and after.
  if (row.bulkSend) {
    return bulkDraftPath(row.bulkSend.id);
  }
  const send = outboxRowDisplaySend(row);
  if (send?.workspaceId) {
    return workspaceArtifactHref(send.workspaceId, send.workspaceArtifactId, send.artifactVersion);
  }
  if (row.draft?.workspaceId && row.draft.workspaceArtifactId) {
    return workspaceArtifactHref(
      row.draft.workspaceId,
      row.draft.workspaceArtifactId,
      row.draft.artifactVersion
    );
  }
  if (row.artifact) {
    return workspaceArtifactHref(
      row.artifact.metadata.workspaceId,
      row.artifact.id,
      row.artifact.latestVersion
    );
  }
  return undefined;
};

/**
 * Where sending again happens: the composer, opened on the email that failed.
 *
 * The outbox has no send of its own and must not grow one: every guard that
 * makes a send safe lives in the composer.
 *
 * A failed send leaves all of that open on purpose: FAILED is outside the
 * one-send-per-version index and outside the artifact's edit lock, so the email
 * can be sent again from where it was written. Undefined where the workspace is
 * gone and there is nothing to reopen.
 *
 * Withdrawn entirely once any attempt reached the recipient: sending again
 * would deliver a second copy of an email the client already has. The row still
 * opens the composer; what is withdrawn is the invitation.
 */
export const outboxRowRetryHref = (row: OutboxRow, opts: { nowMs: number }): string | undefined =>
  outboxRowStatus(row, opts) === 'failed' && !outboxRowDelivered(row)
    ? outboxRowArtifactHref(row)
    : undefined;

/**
 * The mailboxes this reader could open a message inside, out of the accounts
 * the server says they may send from.
 *
 * Ownership, not send-as, and the gap between the two is the whole point. A
 * provider deep link resolves against whatever mail session the browser already
 * has, so offering one is a claim that the reader is signed into that mailbox.
 * The only fact Cara holds that carries such a claim is ownership: the owner is
 * the person who completed the consent with their own provider account. Being
 * named in a mailbox's sharing is a grant Cara made and the provider knows
 * nothing about, so a colleague may be free to send as service@agency.test and
 * still have no way to open its Drafts folder.
 *
 * An account missing from `accounts` is not owned by the reader either, since
 * an owner may always send as their own mailbox and so is never filtered out of
 * the list. Absence is a sound "no" rather than a hole in what is known.
 */
export const openableMailboxAccountIds = (
  accounts: Pick<MailboxSharingFieldsFragment, 'id' | 'owner'>[],
  currentAgencyUserId: string
): ReadonlySet<string> =>
  new Set(
    accounts
      .filter((account) => account.owner.agencyUserId === currentAgencyUserId)
      .map((account) => account.id)
  );

/**
 * The way into the provider's own copy, offered only on rows whose mailbox this
 * reader could open.
 *
 * A deep link into a mailbox the reader is not signed into fails at the
 * provider, behind a sign-in page Cara neither wrote nor can explain. Saying
 * nothing is the better answer.
 */
export const outboxRowWebLink = (
  row: OutboxRow,
  openableAccountIds: ReadonlySet<string>
): string | undefined =>
  row.draft && openableAccountIds.has(row.draft.mailboxAccountId)
    ? mailboxDraftWebLink(row.draft)
    : undefined;

/**
 * The time column: the most recent thing that happened to this email, named so
 * that a send time and a draft's creation time are not read as the same event.
 */
export const outboxRowTimestamp = (
  row: OutboxRow
): { label: string; title: string; unconfirmed: boolean } => {
  if (row.outbound?.status === OutboundEmailStatus.Scheduled) {
    return scheduledRowTimestamp(row.outbound);
  }
  if (row.outbound) {
    const { label, title, claimedOnly } = outboundEmailTimestamp(row.outbound);
    return { label, title, unconfirmed: claimedOnly };
  }
  const unreportedSend = outboxRowDisplaySend(row);
  if (unreportedSend) {
    return {
      label: rowDateTimeFormatter.format(new Date(unreportedSend.updatedAt)),
      title: 'When the schedule was cancelled',
      unconfirmed: false
    };
  }
  if (row.draft) {
    return {
      label: rowDateTimeFormatter.format(new Date(row.draft.createdAt)),
      title: 'When Cara wrote the draft into the mailbox',
      unconfirmed: false
    };
  }
  return {
    label: rowDateTimeFormatter.format(row.sortAt),
    title: 'When Cara composed the email',
    unconfirmed: false
  };
};

export const OutboxStatusBadge = ({
  status,
  unconfirmed = false,
  unknown = false,
  isQueued = false,
  heldBack,
  title
}: {
  status: OutboxStatus;
  unconfirmed?: boolean;
  /** Whether the status is read from a ledger that could not be read. */
  unknown?: boolean;
  /** Waiting its turn in a Send all, which has no send time of the agent's choosing. */
  isQueued?: boolean;
  /** Set when its batch's Send left this composed email out, with why when there's a reason left. */
  heldBack?: { reason: string | null };
  /** Replaces the status's own hover text. */
  title?: string;
}) => {
  // First, because every branch below states a fact about the email and this
  // one says there is no such fact to state.
  if (unknown) {
    return (
      <Badge color="amber" title={UNKNOWN_TITLE}>
        {UNKNOWN_LABEL}
      </Badge>
    );
  }

  if (status === 'checking') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400"
        title={STATUS_TITLE.checking}
      >
        <Spinner size="size-3.5" color="neutral" />
        {STATUS_LABEL.checking}
      </span>
    );
  }

  if (status === 'scheduled' && isQueued) {
    return (
      <Badge color="neutral" title="Waiting its turn to send.">
        Queued
      </Badge>
    );
  }

  if (status === 'composed' && heldBack) {
    const reason = heldBack.reason ?? HELD_BACK_FIXED_TITLE;
    return (
      <Badge color="amber" title={reason}>
        Issue<span className="sr-only">. {reason}</span>
      </Badge>
    );
  }

  if (status === 'failed' && unconfirmed) {
    return (
      <Badge color="amber" title="Cara could not establish whether this email went out">
        {UNCONFIRMED_LABEL}
      </Badge>
    );
  }

  return (
    <Badge color={STATUS_COLOR[status]} title={title ?? STATUS_TITLE[status]}>
      {outboxStatusLabel(status)}
    </Badge>
  );
};

/**
 * Who the email belongs to. The person, on one line; the address they sent it
 * from rides in the hover text: a column two lines tall on the sent rows and
 * one on the rest cannot be skimmed, and the address is the half a reader can
 * reconstruct.
 */
const OutboxSender = ({ name, address }: { name: string | null; address: string }) => {
  if (!name && !address) {
    return <Dash />;
  }
  return (
    <span
      className="block max-w-[13rem] truncate text-neutral-700 dark:text-neutral-300"
      title={[name, address].filter(Boolean).join(' · ')}
    >
      {name || address}
    </span>
  );
};

const BulkDraftStatusBadge = ({ bulkDraft }: { bulkDraft: OutboxBulkSend }) => {
  if (!isBulkSendDrafting(bulkDraft)) {
    return (
      <Badge color={STATUS_COLOR.composed} title="Drafted. Open it to send.">
        {STATUS_LABEL.composed}
      </Badge>
    );
  }
  const { finished, total } = bulkDraft.draftProgress;
  return (
    <Badge color={STATUS_COLOR.sending}>
      <Spinner size="size-3.5" color="blue" />
      Drafting {finished} of {total}
    </Badge>
  );
};

const BATCH_STATUS_COLOR: Record<
  BatchStatus['kind'],
  'sky' | 'blue' | 'green' | 'red' | 'neutral'
> = {
  sending: 'blue',
  waiting: 'neutral',
  scheduled: 'sky',
  sent: 'green',
  failed: 'red',
  cancelled: 'neutral',
  inDrafts: 'sky'
};

/** A bulk email's Status: its drafting before Send, how far its batch has got after. */
const BulkSendStatusBadge = ({ bulkSend, nowMs }: { bulkSend: OutboxBulkSend; nowMs: number }) => {
  if (!bulkSend.sentAt) {
    return <BulkDraftStatusBadge bulkDraft={bulkSend} />;
  }
  const status = batchStatus(bulkSend, nowMs);
  // A polite live region, so a screen reader hears the count move while the batch sends.
  return (
    <span role="status">
      <Badge color={BATCH_STATUS_COLOR[status.kind]} title={status.title}>
        {status.kind === 'sending' && <Spinner size="size-3.5" color="blue" />}
        {status.label}
      </Badge>
    </span>
  );
};

/** What a sent bulk email's Last activity is the time of. */
const BULK_SEND_TIME_TITLE: Record<EmailBulkSendAction, string> = {
  [EmailBulkSendAction.SendAll]: 'When Send all was clicked',
  [EmailBulkSendAction.ScheduleAll]: 'When Schedule all was clicked',
  [EmailBulkSendAction.MoveToDrafts]: 'When Move all to drafts was clicked'
};

type OutboxColumnId =
  | 'status'
  | 'subject'
  | 'recipients'
  | 'ams'
  | 'person'
  | 'updated'
  | 'openInMailbox';

/** What the AMS column reads: each row's AMS log, and the way to push its email. */
export type OutboxAmsColumn = {
  amsLogOf: (row: OutboxRow) => OutboundEmailAmsLogFieldsFragment | null;
  isAmsLogUnread: (row: OutboxRow) => boolean;
  onPush: (row: OutboxRow) => void;
};

/**
 * A bulk email's row: the bulk email, not one of its emails. Its mailbox is chosen at Send, so
 * From is a dash before then and the batch's from address after. Nothing of its own is in a
 * mailbox to open.
 */
const BULK_SEND_CELLS: Record<
  OutboxColumnId,
  (bulkSend: OutboxBulkSend, row: OutboxRow, nowMs: number) => ReactNode
> = {
  status: (bulkSend, _row, nowMs) => <BulkSendStatusBadge bulkSend={bulkSend} nowMs={nowMs} />,
  subject: (bulkSend) => (
    <div className="flex max-w-xl items-center gap-2">
      <span className="min-w-0 max-w-xs truncate font-medium" title={bulkSend.name}>
        {bulkSend.name}
      </span>
      <BulkBadge />
    </div>
  ),
  recipients: (bulkSend) => (
    <span className="text-neutral-700 dark:text-neutral-300">
      {bulkSend.recipients.length} {pluralize(bulkSend.recipients.length, 'recipient')}
    </span>
  ),
  ams: () => <Dash />,
  person: (bulkSend) => <OutboxSender name={null} address={bulkSend.fromAddress ?? ''} />,
  updated: (bulkSend, row) => (
    <span
      className="text-neutral-700 dark:text-neutral-300"
      title={
        bulkSend.action ? BULK_SEND_TIME_TITLE[bulkSend.action] : 'When Cara started the bulk draft'
      }
    >
      {rowDateTimeFormatter.format(row.sortAt)}
    </span>
  ),
  openInMailbox: () => null
};

// The cell holds Push, which mustn't also open the row's email.
const amsColumn = (ams: OutboxAmsColumn): ColumnDef<OutboxRow> & { id: OutboxColumnId } => ({
  id: 'ams',
  header: 'AMS',
  cellClassName: 'align-middle',
  noHref: true,
  renderCell: (row) => (
    <OutboxAmsCell
      amsLog={ams.amsLogOf(row)}
      isUnread={ams.isAmsLogUnread(row)}
      subject={outboxRowSubject(row) || undefined}
      onPush={() => ams.onPush(row)}
    />
  )
});

const NOTICE_TONE_CLASS: Record<OutboxNoticeTone, string> = {
  failure: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-700 dark:text-amber-300',
  neutral: 'text-neutral-500 dark:text-neutral-400'
};

export function getOutboxColumns(opts: {
  nowMs: number;
  agencyUsers: Pick<AgencyUserFieldsFragment, 'id' | 'name' | 'email'>[];
  checkingDraftIds: ReadonlySet<string>;
  /**
   * Drafts whose mailbox cannot be asked until it is reconnected. Absent — in a
   * test rendering cells on their own, say — reads every mailbox as reachable,
   * which is the shape these cells had before a look could say otherwise.
   */
  disconnectedDraftIds?: ReadonlySet<string>;
  /**
   * Asks the page to put the double-send warning in front of the reader.
   * Absent — in a test rendering cells on their own, say — leaves the row
   * saying what it knows and offering nothing to click, which is the safe
   * shape of a missing confirmation step.
   */
  onRetryUnconfirmed?: (row: OutboxRow) => void;
  /**
   * The mailboxes the reader could open a provider link into. Absent — in a
   * test rendering cells on their own, say — offers the link on no row at all,
   * which is the safe shape of not knowing.
   */
  openableMailboxAccountIds?: ReadonlySet<string>;
  /**
   * The ledgers whose read failed. Absent — in a test rendering cells on their
   * own, say — reads every row as standing on a complete record, which is the
   * shape these cells had before any read could fail.
   */
  unreadSources?: OutboxUnreadSources;
  /** Each mailbox's provider, to name it in "Reconnect Outlook". */
  mailboxProviders?: ReadonlyMap<string, MailboxProvider>;
  /** Each mailbox's address, for the From of an email not yet in a mailbox. */
  mailboxAddresses?: ReadonlyMap<string, string>;
  /** The agency's recent batches, for their emails' Queued badge. */
  batches?: OutboxBatches;
  /** The AMS column, left out when no AMS logs the agency's emails. */
  ams?: OutboxAmsColumn;
}): ColumnDef<OutboxRow>[] {
  const { nowMs, agencyUsers, checkingDraftIds, onRetryUnconfirmed } = opts;
  const openableAccountIds = opts.openableMailboxAccountIds ?? NO_OPENABLE_MAILBOXES;
  const mailboxProviders = opts.mailboxProviders ?? NO_MAILBOX_PROVIDERS;
  const unreadSources = opts.unreadSources ?? EVERY_LEDGER_READ;
  const disconnectedDraftIds = opts.disconnectedDraftIds ?? NO_DISCONNECTED_MAILBOXES;

  const statusOf = (row: OutboxRow) =>
    outboxRowStatus(row, {
      nowMs,
      checking: !!row.draft && checkingDraftIds.has(row.draft.id),
      mailboxDisconnected: !!row.draft && disconnectedDraftIds.has(row.draft.id)
    });
  const unknownOf = (row: OutboxRow) => outboxRowStatusUnsound(row, unreadSources);
  const batches = opts.batches ?? NO_OUTBOX_BATCHES;
  const mailboxProviderOf = (row: OutboxRow) =>
    mailboxProviders.get(row.outbound?.mailboxAccountId ?? row.draft?.mailboxAccountId ?? '');

  const columns: (ColumnDef<OutboxRow> & { id: OutboxColumnId })[] = [
    {
      id: 'status',
      header: 'Status',
      cellClassName: 'align-middle',
      renderCell: (row) => {
        const statusNote = unknownOf(row) ? null : scheduledSendStatusNote(row.outbound);
        return (
          <>
            <OutboxStatusBadge
              status={statusOf(row)}
              unconfirmed={outboxRowSendUnconfirmed(row)}
              unknown={unknownOf(row)}
              isQueued={isOutboxRowQueuedInBatch(row, batches)}
              heldBack={
                isOutboxRowHeldBack(row)
                  ? { reason: row.artifact?.heldBackReason ?? null }
                  : undefined
              }
            />
            {statusNote && (
              <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                {statusNote}
              </span>
            )}
          </>
        );
      }
    },
    {
      id: 'subject',
      header: 'Subject',
      // max-w-0 drops the subject's text from the cell's minimum width, so the column shrinks
      // and truncates instead of pushing the last column off the table.
      cellClassName: 'align-middle w-full max-w-0',
      renderCell: (row) => {
        // A row with no settled account of itself to give has nothing to say
        // beside the subject either: every notice describes where the email
        // stood, which is the question being re-read or the question a failed
        // read left open.
        const unsettled = statusOf(row) === 'checking' || unknownOf(row);
        const provider = mailboxProviderOf(row);
        const { lines, shownLine, tone } = unsettled
          ? { lines: [], shownLine: null, tone: 'neutral' as const }
          : outboxRowNotices(row, {
              nowMs,
              mailboxDisconnected: !!row.draft && disconnectedDraftIds.has(row.draft.id),
              mailClient: provider ? mailClientName(provider) : undefined
            });
        const subject = outboxRowSubject(row);
        const attachments = row.outbound?.attachments ?? [];
        // A row's first notice sits under its subject. The pair is 36px tall;
        // the negative margin dips 6px into the cell's padding at either end,
        // so the row stays the height of a one-line row.
        const hasNotice = lines.length > 0;

        return (
          <div className="flex max-w-xl items-center gap-2">
            {/* The subject gives up width before the badges and actions beside it do,
                so a narrow window truncates it rather than clipping the last column. */}
            {hasNotice ? (
              <div className="-my-1.5 min-w-0 max-w-xs">
                <span className="block truncate font-medium leading-5" title={subject || undefined}>
                  {subject ? <FieldReferenceText text={subject} /> : <Dash />}
                </span>
                <span
                  className={clsx('block truncate text-xs leading-4', NOTICE_TONE_CLASS[tone])}
                  title={lines.join('\n\n')}
                >
                  {shownLine}
                </span>
              </div>
            ) : (
              <span className="min-w-0 max-w-xs truncate font-medium" title={subject || undefined}>
                {subject ? <FieldReferenceText text={subject} /> : <Dash />}
              </span>
            )}
            {attachments.length > 0 && (
              <span
                className="flex shrink-0 items-center gap-0.5 text-xs text-neutral-500 dark:text-neutral-400"
                title={attachments.map((attachment) => attachment.name).join(', ')}
              >
                <PaperClipIcon className="size-3.5" aria-hidden />
                {attachments.length}
              </span>
            )}
            <OutboxReconnectAction row={row} nowMs={nowMs} mailboxProviders={mailboxProviders} />
            <OutboxRetryAction row={row} nowMs={nowMs} onRetryUnconfirmed={onRetryUnconfirmed} />
          </div>
        );
      }
    },
    {
      id: 'recipients',
      header: 'Recipients',
      cellClassName: 'align-middle',
      renderCell: (row) => {
        const recipients = outboxRowRecipients(row);
        if (!recipients) {
          return <Dash />;
        }

        return (
          <div className="flex max-w-[13rem] items-center gap-1 text-neutral-700 dark:text-neutral-300">
            <span className="truncate" title={recipients.title}>
              {recipients.label}
            </span>
            {recipients.extra > 0 && (
              <span
                className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400"
                title={recipients.title}
              >
                +{recipients.extra}
              </span>
            )}
          </div>
        );
      }
    },
    ...(opts.ams ? [amsColumn(opts.ams)] : []),
    {
      id: 'person',
      header: 'From',
      cellClassName: 'align-middle',
      renderCell: (row) => (
        <OutboxSender
          name={outboxRowSenderName(row, agencyUsers)}
          address={outboxRowFromAddress(row, opts.mailboxAddresses)}
        />
      )
    },
    {
      id: 'updated',
      header: 'Last activity',
      cellClassName: 'align-middle whitespace-nowrap',
      renderCell: (row) => {
        const { label, title, unconfirmed } = outboxRowTimestamp(row);

        return (
          <span
            className={
              unconfirmed
                ? 'text-neutral-500 dark:text-neutral-400 italic'
                : 'text-neutral-700 dark:text-neutral-300'
            }
            title={title}
          >
            {label}
          </span>
        );
      }
    },
    {
      id: 'openInMailbox',
      header: 'Open',
      align: 'right',
      cellClassName: 'align-middle',
      // The row itself opens the workspace artifact; this cell opens the
      // provider's copy, which is a different destination and so cannot be part
      // of the row link. The link cannot name the provider: a row carries a
      // mailbox account id, not the account, so the label stays generic even
      // though the reader is known to own the mailbox behind it.
      noHref: true,
      // Nothing at all where there is no link, rather than the dash the data
      // columns use. A dash stands in for a value that is not on file; an
      // action that is not on offer has no value to stand in for, and a column
      // of them on the rows of an agency whose mailboxes belong to somebody
      // else reads as a fault in the page.
      renderCell: (row) => {
        const href = outboxRowWebLink(row, openableAccountIds);
        if (!href) {
          return null;
        }

        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
          >
            Mailbox
            <ArrowTopRightOnSquareIcon className="size-4" aria-hidden />
          </a>
        );
      }
    }
  ];
  return columns.map((column) => ({
    ...column,
    renderCell: (row) =>
      row.bulkSend ? BULK_SEND_CELLS[column.id](row.bulkSend, row, nowMs) : column.renderCell(row)
  }));
}

/**
 * The way back to the composer for an email whose send failed.
 *
 * Two shapes for the two failures, because they carry different risk. Where
 * Cara knows the mail never left, sending again is an ordinary link. Where it
 * could not establish whether the mail left, the link is replaced by a button
 * that asks first: the cost of a mistaken click is a client receiving the same
 * email twice, which no amount of undo takes back.
 */
const OutboxRetryAction = ({
  row,
  nowMs,
  onRetryUnconfirmed
}: {
  row: OutboxRow;
  nowMs: number;
  onRetryUnconfirmed?: (row: OutboxRow) => void;
}) => {
  const href = outboxRowRetryHref(row, { nowMs });
  if (!href) {
    return null;
  }

  if (outboxRowSendUnconfirmed(row)) {
    if (!onRetryUnconfirmed) {
      return null;
    }
    return (
      <button
        type="button"
        onClick={(event) => {
          // The row is a link to the same workspace; letting the click through
          // would navigate past the warning this button exists to show.
          event.preventDefault();
          event.stopPropagation();
          onRetryUnconfirmed(row);
        }}
        title="This email may already be with the recipient. Cara will ask before opening the composer."
        // The row's own link covers the whole cell as an overlay, so a control
        // below it would be a warning nobody could reach; and it must never
        // shrink or wrap, or the one control on the row would truncate away.
        className="relative z-10 shrink-0 text-xs font-medium whitespace-nowrap text-amber-700 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-200"
      >
        Send again anyway...
      </button>
    );
  }

  return (
    <a
      href={href}
      title="Opens the composer, which is where sending again happens"
      className="relative z-10 shrink-0 text-xs font-medium whitespace-nowrap text-primary-600 dark:text-primary-400 underline hover:text-primary-800 dark:hover:text-primary-300"
    >
      Send again
    </a>
  );
};

/**
 * The way to fix a send refused because the mailbox signed Cara out. Sending
 * again from the composer would only be refused the same way.
 */
const OutboxReconnectAction = ({
  row,
  nowMs,
  mailboxProviders
}: {
  row: OutboxRow;
  nowMs: number;
  mailboxProviders: ReadonlyMap<string, MailboxProvider>;
}) => {
  const isSignedOut =
    outboxRowStatus(row, { nowMs }) === 'failed' &&
    row.outbound?.failureKind === OutboundEmailFailureKind.ReauthRequired;
  if (!isSignedOut) {
    return null;
  }
  return (
    <a
      href="/app/settings/mailbox"
      className={`${OUTBOX_ROW_ACTION_CLASS} text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300`}
    >
      Reconnect {mailClientName(mailboxProviders.get(row.outbound?.mailboxAccountId ?? ''))}
    </a>
  );
};

/**
 * What the reader must weigh before sending an unconfirmed email a second
 * time, in the words of the situation rather than of the code.
 */
export const outboxUnconfirmedRetryWarning = (row: OutboxRow): string => {
  const recipient = row.outbound
    ? outboundEmailRecipientSummary(row.outbound).label
    : 'the recipient';
  return `Cara asked the mail provider to send this email and never learned what happened to it, so ${recipient} may already have it. Sending it again could deliver a second copy. Check the mailbox’s Sent folder first.`;
};
